import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Shield, Star, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';
import { piercingPhotos } from '../mock';
import { photosAPI } from '../services/api';
import AnimatedSlider from '../components/AnimatedSlider';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Home = () => {
  const [sliderPhotos, setSliderPhotos] = useState([]);

  useEffect(() => {
    loadSliderPhotos();
  }, []);

  const loadSliderPhotos = async () => {
    try {
      const data = await photosAPI.getAll();
      const dbPhotos = data.photos.map(p => ({
        id: p.id,
        url: `${BACKEND_URL}${p.url}`,
        title: p.title,
        category: p.category
      }));
      
      // Combine and use first 15 photos for slider
      const allPhotos = [...dbPhotos, ...piercingPhotos];
      setSliderPhotos(allPhotos.slice(0, 15));
    } catch (error) {
      console.error('Error loading photos:', error);
      // Fallback to mock photos
      setSliderPhotos(piercingPhotos.slice(0, 15));
    }
  };

  return (
    <div className="min-h-screen">
      {/* Animated Photo Slider */}
      {sliderPhotos.length > 0 && <AnimatedSlider photos={sliderPhotos} />}

      {/* Why Choose Steve */}
      <section className="py-20 px-4 bg-zinc-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-black text-center mb-16 text-zinc-100">
            WHY CHOOSE STEVE
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-zinc-950 p-8 border border-zinc-800 hover:border-zinc-700 transition-all duration-300 group">
              <div className="mb-6 text-red-800 group-hover:text-red-700 transition-colors">
                <Shield className="h-12 w-12" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-zinc-100">Safe & Sterile</h3>
              <p className="text-zinc-400 leading-relaxed">
                Hospital-grade sterilization, single-use needles, and premium materials for your safety and peace of mind.
              </p>
            </div>
            <div className="bg-zinc-950 p-8 border border-zinc-800 hover:border-zinc-700 transition-all duration-300 group">
              <div className="mb-6 text-red-800 group-hover:text-red-700 transition-colors">
                <Star className="h-12 w-12" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-zinc-100">Expert Artistry</h3>
              <p className="text-zinc-400 leading-relaxed">
                Years of experience with precise placement, proper angles, and attention to your unique anatomy.
              </p>
            </div>
            <div className="bg-zinc-950 p-8 border border-zinc-800 hover:border-zinc-700 transition-all duration-300 group">
              <div className="mb-6 text-red-800 group-hover:text-red-700 transition-colors">
                <Clock className="h-12 w-12" />
              </div>
              <h3 className="text-2xl font-bold mb-4 text-zinc-100">Aftercare Support</h3>
              <p className="text-zinc-400 leading-relaxed">
                Comprehensive aftercare instructions and ongoing support through our client chat system.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Gallery Preview */}
      <section className="py-20 px-4 relative overflow-hidden" style={{ backgroundColor: '#0f0f0f' }}>
        {/* Parallax Background */}
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_stabbed-by-steve/artifacts/azl2xdwh_PIERCINGWALLPAPER.jpg)',
            backgroundSize: '400px 400px',
            backgroundPosition: 'center',
            backgroundAttachment: 'fixed',
            backgroundRepeat: 'repeat',
            transform: 'translateZ(0)',
            willChange: 'transform',
            filter: 'invert(1)'
          }}
        ></div>
        
        <div className="max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <h2 className="text-4xl md:text-5xl font-black mb-4 text-zinc-100">STEVE'S WORK</h2>
            <p className="text-zinc-400 text-lg">Professional piercings done with precision and care</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-12">
            {piercingPhotos.slice(0, 6).map((photo) => (
              <div key={photo.id} className="relative aspect-square overflow-hidden bg-zinc-900 border border-zinc-800 group cursor-pointer">
                <img
                  src={photo.url}
                  alt={photo.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                  <p className="text-white font-bold p-4">{photo.title}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/gallery">
              <Button size="lg" variant="outline" className="border-2 border-zinc-600 text-zinc-100 hover:bg-zinc-800 transition-all duration-300">
                View Full Gallery
                <ChevronRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Quick CTA */}
      <section className="py-20 px-4 bg-gradient-to-br from-red-950 via-zinc-950 to-zinc-950">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-black mb-6 text-zinc-100">
            READY TO GET PIERCED?
          </h2>
          <p className="text-xl text-zinc-300 mb-8">
            Book your appointment today and join our community of satisfied clients
          </p>
          <Link to="/booking">
            <Button size="lg" className="bg-red-800 hover:bg-red-700 text-white px-10 py-6 text-lg font-bold tracking-wide transition-all duration-300 hover:scale-105 shadow-xl shadow-red-900/50">
              Book Now
              <ChevronRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Home;